import { WorkAPI } from '../API'
import { collection } from '../../infra/collection'
import { Insight } from './Insight'
import { Id } from '../../types/definitions'
import {
  GetLatestJobInsightsRequest,
  GenerateJobInsightsRequest,
  Insight as InsightProto,
} from '@taxfyle/api-internal/internal/insights_pb'
import { InsightPropsDto } from './dtos'

export class InsightStore {
  /**
   * Insights for jobs.
   */
  jobInsights = collection<InsightPropsDto, Insight>({
    getDataId: (i) => i.insightId,
    getModelId: (i) => i.id,
    create: () => new Insight(),
  })

  /**
   * Constructor.
   */
  constructor(private readonly api: WorkAPI) {}

  /**
   * Get all the latest insights in the collection for the given job ID.
   * @param jobId
   */
  latestInsightsForJob(jobId: string) {
    return this.jobInsights.filter((i) => i.jobId === jobId)
  }

  /**
   * Fetches the latest Insights for the given job ID.
   * @param jobId
   */
  async fetchLatestJobInsights(jobId: Id): Promise<void> {
    const request = new GetLatestJobInsightsRequest()
    request.setJobId(Id.assert(jobId))

    const res = await this.api.insights.getLatestJobInsights(request)
    const obj = res.toObject()

    this.clearJobInsightsForJobId(jobId)
    this.setJobInsights(jobId, obj.insightsList)
  }

  /**
   * Generate all Insights or refresh all Insights for the given job ID.
   * @param jobId
   */
  async generateOrRefreshJobInsights(jobId: Id): Promise<void> {
    const request = new GenerateJobInsightsRequest()
    request.setJobId(Id.assert(jobId))
    // Currently we only offer 5 insights in total, so lets just return all of them.
    request.setPageSize(5)

    await this.api.insights.generateJobInsights(request)

    await this.fetchLatestJobInsights(jobId)
  }

  /**
   * Set the Job Insights to the collection.
   * @param jobId
   * @param insights
   */
  private setJobInsights(jobId: Id, insights: InsightProto.AsObject[]) {
    insights.forEach((insight) => {
      this.jobInsights.set({ jobId, ...insight })
    })
  }

  /**
   * Clears all the Job Insights for the given job ID.
   * @param jobId
   */
  private clearJobInsightsForJobId(jobId: Id): void {
    this.jobInsights
      .filter((i) => i.jobId === jobId)
      .forEach((insight) => {
        this.jobInsights.remove(insight)
      })
  }
}
